/**
 * LinkedInSignInButton v1.0 (2026-09-29, cc_tasks row 260, T299)
 * "Continue with LinkedIn", placed beside Continue with Google on each sign-in surface.
 * The destination-keeping logic lives in src/lib/linkedin-signin.ts.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { LINKEDIN_TOOLTIP, startLinkedInSignIn } from "@/lib/linkedin-signin";

export const LinkedInIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#0A66C2"
      d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z"
    />
  </svg>
);

export function LinkedInSignInButton({ className = "", persona, returnTo, variant = "default" }: { className?: string; persona?: string; returnTo?: string; variant?: "default" | "outline" }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const err = await startLinkedInSignIn({ persona, returnTo });
      if (err) toast({ title: "LinkedIn sign in failed", description: err, variant: "destructive" });
    } catch (e) {
      toast({ title: "LinkedIn sign in failed", description: e instanceof Error ? e.message : "Please try again.", variant: "destructive" });
    } finally {
      // On success the page is already leaving for LinkedIn.
      setBusy(false);
    }
  };
  return (
    <Button
      type="button"
      variant={variant}
      onClick={onClick}
      disabled={busy}
      title={LINKEDIN_TOOLTIP}
      aria-label="Continue with LinkedIn"
      className={className}
    >
      {busy ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <>
          <LinkedInIcon />
          <span className="ml-2">Continue with LinkedIn</span>
        </>
      )}
    </Button>
  );
}
